import React, { useState } from 'react';
import { ArrowLeft, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import RestApi from '../../Apis/RestApi';

const SmartLinkForm = () => {
  const [formData, setFormData] = useState({
    inputLink: '',
    numberOfCharacter: '6',
    name: '',
    totalLinks: '1',
    fileType: 'csv',
    singleUse: false,
    batches: false,
    numberOfBatches: '',
    itemsPerBatch: '',
    customization: ''
  });

  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error for this field if it exists
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = {...prev};
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.inputLink.trim()) {
      newErrors.inputLink = 'Input link is required';
    }
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.totalLinks || parseInt(formData.totalLinks) < 1) {
      newErrors.totalLinks = 'Number of links must be at least 1';
    }
    
    if (formData.batches) {
      if (!formData.numberOfBatches || parseInt(formData.numberOfBatches) < 1) {
        newErrors.numberOfBatches = 'Number of batches must be at least 1';
      }
      
      if (!formData.itemsPerBatch || parseInt(formData.itemsPerBatch) < 1) {
        newErrors.itemsPerBatch = 'Items per batch must be at least 1';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Calculate total links if using batches
    const calculatedTotalLinks = formData.batches ? 
      parseInt(formData.numberOfBatches) * parseInt(formData.itemsPerBatch) : 
      parseInt(formData.totalLinks);
    
    // Prepare data for API call
    const apiData = {
      inputLink: formData.inputLink,
      name: formData.name,
      user:localStorage.getItem("_id"),
      numberOfCharacter: parseInt(formData.numberOfCharacter),
      singleUse: formData.singleUse,
      batches: formData.batches,
      numberOfBatches: formData.batches ? parseInt(formData.numberOfBatches) : undefined,
      itemsPerBatch: formData.batches ? parseInt(formData.itemsPerBatch) : undefined,
      totalLinks: calculatedTotalLinks,
      customization: formData.customization
    };
    
    try {
      const response = await RestApi.createMultipleSmartlinks(apiData);
    } catch (error) {
      console.error('Error creating smart links:', error);
      setErrors({ submit: 'Failed to create smart links. Please try again.' });
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const calculateTotalLinks = () => {
    if (formData.batches && formData.numberOfBatches && formData.itemsPerBatch) {
      return parseInt(formData.numberOfBatches) * parseInt(formData.itemsPerBatch);
    }
    return parseInt(formData.totalLinks) || 0;
  };

  return (
    <div className="min-h-screen bg-[#0f172a]">
      {/* Navigation Bar */}
      <div className="bg-white p-4 shadow-md">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <h1 className="text-xl font-semibold text-gray-800">Manage Smart Links</h1>
        </div>
      </div>

      {/* Form Container */}
      <div className="flex items-center justify-center p-4">
        <div className="w-full max-w-3xl bg-white rounded-lg shadow-xl p-8 h-[90vh] overflow-y-auto">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6">Smart Link Generator</h2>
          
          {errors.submit && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {errors.submit}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Input Link */}
            <div>
              <label htmlFor="inputLink" className="block text-sm font-medium text-gray-700 mb-1">Destination URL</label>
              <input
                type="text"
                id="inputLink"
                name="inputLink"
                value={formData.inputLink}
                onChange={handleInputChange}
                placeholder="Enter the URL you want to redirect to"
                className={`w-full p-3 border-2 ${errors.inputLink ? 'border-red-500' : 'border-gray-300'} rounded text-black focus:outline-none focus:border-blue-500`}
              />
              {errors.inputLink && <p className="mt-1 text-red-500 text-sm">{errors.inputLink}</p>}
            </div>

            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Base Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter a base name for your links"
                className={`w-full p-3 border-2 ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded text-black focus:outline-none focus:border-blue-500`}
              />
              {errors.name && <p className="mt-1 text-red-500 text-sm">{errors.name}</p>}
            </div>

            {/* Number of Links */}
            <div>
              <label htmlFor="totalLinks" className="block text-sm font-medium text-gray-700 mb-1">Number of Links to Create</label>
              <input
                type="number"
                id="totalLinks"
                name="totalLinks"
                value={formData.totalLinks}
                onChange={handleInputChange}
                min="1"
                disabled={formData.batches}
                className={`w-full p-3 border-2 ${errors.totalLinks ? 'border-red-500' : 'border-gray-300'} rounded text-black focus:outline-none focus:border-blue-500 ${formData.batches ? 'bg-gray-100' : ''}`}
              />
              {errors.totalLinks && <p className="mt-1 text-red-500 text-sm">{errors.totalLinks}</p>}
              {formData.batches && <p className="mt-1 text-gray-500 text-sm">Total links will be calculated from batch settings</p>}
            </div>

            {/* Number of character */}
            <div>
              <label htmlFor="numberOfCharacter" className="block text-sm font-medium text-gray-700 mb-1">Short Link Character Length</label>
              <select
                id="numberOfCharacter"
                name="numberOfCharacter"
                value={formData.numberOfCharacter}
                onChange={handleInputChange}
                className="w-full p-3 border-2 border-gray-300 rounded text-black focus:outline-none focus:border-blue-500"
              >
                <option value="3">3 characters</option>
                <option value="4">4 characters</option>
                <option value="5">5 characters</option>
                <option value="6">6 characters (recommended)</option>
              </select>
              <p className="mt-1 text-gray-500 text-xs">Short links will be in the format: https://sq-c.in/[characters]</p>
            </div>

            {/* Single Use Button */}
            <div className="flex items-center gap-2 p-3 border-2 border-gray-300 rounded">
              <input
                type="checkbox"
                id="singleUse"
                name="singleUse"
                checked={formData.singleUse}
                onChange={handleInputChange}
                className="w-5 h-5 rounded border-gray-300"
              />
              <label htmlFor="singleUse" className="text-gray-700">Single Use Links</label>
              <div className="ml-auto flex items-center text-gray-500 text-sm">
                <Info className="w-4 h-4 mr-1" />
                <span>Links will expire after one use</span>
              </div>
            </div>

            {/* Export Format */}
            <div>
              <label htmlFor="fileType" className="block text-sm font-medium text-gray-700 mb-1">Export Format</label>
              <select
                id="fileType"
                name="fileType"
                value={formData.fileType}
                onChange={handleInputChange}
                className="w-full p-3 border-2 border-gray-300 rounded text-black focus:outline-none focus:border-blue-500"
              >
                <option value="csv">CSV</option>
                <option value="xlsx">Excel (XLSX)</option>
                <option value="json">JSON</option>
              </select>
            </div>

            {/* Batches Checkbox and Conditional Fields */}
            <div className="p-4 border-2 border-gray-300 rounded">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="batches"
                  name="batches"
                  checked={formData.batches}
                  onChange={handleInputChange}
                  className="w-5 h-5 rounded border-gray-300"
                />
                <label htmlFor="batches" className="text-gray-700 font-medium">Enable Batch Processing</label>
              </div>
              
              <p className="mt-1 mb-3 text-gray-500 text-sm">Create links in organized batches with sequential naming</p>

              {formData.batches && (
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="numberOfBatches" className="block text-sm font-medium text-gray-700 mb-1">Number of Batches</label>
                      <input
                        type="number"
                        id="numberOfBatches"
                        name="numberOfBatches"
                        value={formData.numberOfBatches}
                        onChange={handleInputChange}
                        min="1"
                        placeholder="e.g., 5"
                        className={`w-full p-3 border-2 ${errors.numberOfBatches ? 'border-red-500' : 'border-gray-300'} rounded text-black focus:outline-none focus:border-blue-500`}
                      />
                      {errors.numberOfBatches && <p className="mt-1 text-red-500 text-sm">{errors.numberOfBatches}</p>}
                    </div>
                    <div>
                      <label htmlFor="itemsPerBatch" className="block text-sm font-medium text-gray-700 mb-1">Items per Batch</label>
                      <input
                        type="number"
                        id="itemsPerBatch"
                        name="itemsPerBatch"
                        value={formData.itemsPerBatch}
                        onChange={handleInputChange}
                        min="1"
                        placeholder="e.g., 10"
                        className={`w-full p-3 border-2 ${errors.itemsPerBatch ? 'border-red-500' : 'border-gray-300'} rounded text-black focus:outline-none focus:border-blue-500`}
                      />
                      {errors.itemsPerBatch && <p className="mt-1 text-red-500 text-sm">{errors.itemsPerBatch}</p>}
                    </div>
                  </div>
                  
                  {formData.numberOfBatches && formData.itemsPerBatch && (
                    <div className="p-3 bg-blue-50 rounded border border-blue-200 text-blue-800">
                      <p className="font-medium">Total links to create: {calculateTotalLinks()}</p>
                      <p className="text-sm">Example naming: {formData.name}_B1_1, {formData.name}_B1_2, etc.</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            

            {/* Bottom Section */}
            <div className="flex justify-between items-center mt-8 pt-4 border-t border-gray-200">
              <div className="border-2 border-gray-300 rounded px-4 py-2 text-gray-700 bg-gray-50">
                <span className="text-sm font-medium">Total Links: </span>
                <span className="font-bold">{calculateTotalLinks()}</span>
              </div>

              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={handleBack}
                  className="bg-gray-100 text-gray-800 px-6 py-2 rounded hover:bg-gray-200 transition-colors border border-gray-300"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="bg-blue-600 text-white px-8 py-2 rounded hover:bg-blue-700 transition-colors"
                >
                  Generate Links
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SmartLinkForm;